export type HoneypotId = 'web' | 'api' | 'admin' | 'ssh' | 'database' | 'iot' | 'network';

export type HoneypotStatus = 'implemented' | 'coming-soon';

export type HoneypotDifficulty = 'easy' | 'medium' | 'hard';

export type HoneypotValue = 'low' | 'medium' | 'high' | 'very-high';

export interface HoneypotType {
  id: HoneypotId;
  name: string;
  status: HoneypotStatus;
  description: string;
  features: string[];
  price: number;
  icon: string;
  difficulty: HoneypotDifficulty;
  value: HoneypotValue;
}

export const HONEYPOT_NAMES: Record<HoneypotId, { name: string; icon: string }> = {
  web: { name: 'Web Honeypot', icon: '🌐' },
  api: { name: 'API Honeypot', icon: '🔌' },
  admin: { name: 'Admin Panel Honeypot', icon: '👨‍💼' },
  ssh: { name: 'SSH Honeypot', icon: '🔐' },
  database: { name: 'Database Honeypot', icon: '🗄️' },
  iot: { name: 'IoT Honeypot', icon: '📱' },
  network: { name: 'Network Honeypot', icon: '🌐' }
};

export const HONEYPOT_TYPES: HoneypotType[] = [
  {
    id: 'web',
    name: 'Web Honeypot',
    status: 'implemented',
    description: 'Piège les attaquants avec des pages web factices et des formulaires attractifs',
    features: [
      'Fake login pages',
      'Honeytokens in forms',
      'Credential harvesting detection',
      'XSS trap monitoring'
    ],
    price: 0,
    icon: '🌐',
    difficulty: 'easy',
    value: 'high'
  },
  {
    id: 'api',
    name: 'API Honeypot',
    status: 'coming-soon',
    description: 'Endpoints API fictifs pour détecter les scans automatisés et les exploits',
    features: [
      'Fake REST/GraphQL endpoints',
      'Authentication bypass detection',
      'Rate limiting traps',
      'API key harvesting'
    ],
    price: 49,
    icon: '🔌',
    difficulty: 'medium',
    value: 'high'
  },
  {
    id: 'admin',
    name: 'Admin Panel Honeypot',
    status: 'coming-soon',
    description: "Faux panneaux d'administration pour piéger les attaquants cherchant des accès privilégiés",
    features: [
      'Fake admin dashboards',
      'Brute force detection',
      'Privilege escalation traps',
      'Session hijacking alerts'
    ],
    price: 39,
    icon: '👨‍💼',
    difficulty: 'easy',
    value: 'very-high'
  },
  {
    id: 'ssh',
    name: 'SSH Honeypot',
    status: 'coming-soon',
    description: "Serveur SSH factice pour capturer les tentatives d'intrusion et les credentials",
    features: [
      'Fake SSH server',
      'Brute force monitoring',
      'Command logging',
      'Attacker fingerprinting'
    ],
    price: 79,
    icon: '🔐',
    difficulty: 'medium',
    value: 'very-high'
  },
  {
    id: 'database',
    name: 'Database Honeypot',
    status: 'coming-soon',
    description: 'Base de données factice pour détecter les injections SQL et les accès non autorisés',
    features: [
      'Fake MySQL/PostgreSQL instances',
      'SQL injection detection',
      'Query logging',
      'Data exfiltration alerts'
    ],
    price: 59,
    icon: '🗄️',
    difficulty: 'easy',
    value: 'medium'
  },
  {
    id: 'iot',
    name: 'IoT Honeypot',
    status: 'coming-soon',
    description: "Appareils IoT fictifs pour attirer les botnets et les attaques ciblant l'IoT",
    features: [
      'Fake IoT devices',
      'Botnet detection',
      'DDoS prevention',
      'Malware capture'
    ],
    price: 69,
    icon: '📱',
    difficulty: 'medium',
    value: 'very-high'
  },
  {
    id: 'network',
    name: 'Network Honeypot',
    status: 'coming-soon',
    description: 'Services réseau factices pour détecter les scans de ports et les attaques réseau',
    features: [
      'Multi-protocol support',
      'Port scanning detection',
      'Network mapping alerts',
      'Protocol exploitation traps'
    ],
    price: 99,
    icon: '🌐',
    difficulty: 'hard',
    value: 'medium'
  }
];
